import React, { useState, useEffect, Component, ErrorInfo, ReactNode, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Node } from 'reactflow';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ApprovalConfig from './configs/ApprovalConfig';
import ApprovalConfigSimple from './configs/ApprovalConfigSimple';
import AssignmentConfig from './configs/AssignmentConfig';
import NotificationConfig from './configs/NotificationConfig';
import ConditionConfig from './configs/ConditionConfig';
import ScriptConfig from './configs/ScriptConfig';
import WaitConfig from './configs/WaitConfig';

interface PropertyModalProps {
  node: Node | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (nodeId: string, data: any) => void;
  isReadOnly: boolean;
}

// Error boundary for config components
class ConfigErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Config component error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

const PropertyModal: React.FC<PropertyModalProps> = ({
  node,
  isOpen,
  onClose,
  onUpdate,
  isReadOnly,
}) => {
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [configuration, setConfiguration] = useState<any>({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (node) {
      setLabel(node.data?.label || '');
      setDescription(node.data?.description || '');
      setConfiguration(node.data?.configuration || {});
      setHasChanges(false);
    }
  }, [node]);

  const handleSave = () => {
    if (node) {
      onUpdate(node.id, {
        label,
        description,
        configuration,
        activityType: node.data?.activityType, // Preserve activityType
      });
      setHasChanges(false);
      onClose();
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      const confirmClose = window.confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmClose) return;
    }
    onClose();
  };

  const handleFieldChange = (field: string, value: any) => {
    setHasChanges(true);
    if (field === 'label') setLabel(value);
    else if (field === 'description') setDescription(value);
  };

  const handleConfigurationChange = (newConfig: any) => {
    setHasChanges(true);
    setConfiguration(newConfig);
  };

  const renderConfigurationForm = () => {
    if (!node) return null;
    const activityType = node.data?.activityType;

    switch (activityType) {
      case 'APPROVAL':
        return (
          <ConfigErrorBoundary
            fallback={
              <ApprovalConfigSimple
                configuration={configuration}
                onChange={handleConfigurationChange}
                isReadOnly={isReadOnly}
              />
            }
          >
            <ApprovalConfig
              configuration={configuration}
              onChange={handleConfigurationChange}
              isReadOnly={isReadOnly}
            />
          </ConfigErrorBoundary>
        );
      case 'ASSIGNMENT':
        return (
          <AssignmentConfig
            configuration={configuration}
            onChange={handleConfigurationChange}
            isReadOnly={isReadOnly}
          />
        );
      case 'NOTIFICATION':
        return (
          <NotificationConfig
            configuration={configuration}
            onChange={handleConfigurationChange}
            isReadOnly={isReadOnly}
          />
        );
      case 'SCRIPT':
        return (
          <ScriptConfig
            configuration={configuration}
            onChange={handleConfigurationChange}
            isReadOnly={isReadOnly}
          />
        );
      case 'WAIT':
        return (
          <WaitConfig
            configuration={configuration}
            onChange={handleConfigurationChange}
            isReadOnly={isReadOnly}
          />
        );
      default:
        if (node.type === 'CONDITION') {
          return (
            <ConditionConfig
              configuration={configuration}
              onChange={handleConfigurationChange}
              isReadOnly={isReadOnly}
            />
          );
        }
        return null;
    }
  };

  // Determine modal size based on activity type
  const getModalSize = () => {
    if (!node) return 'max-w-2xl';
    const activityType = node.data?.activityType;
    
    // Larger modal for script editing
    if (activityType === 'SCRIPT') return 'max-w-5xl';
    // Medium-large for approval (has multi-select dropdowns)
    if (activityType === 'APPROVAL') return 'max-w-3xl';
    // Default size for others
    return 'max-w-2xl';
  };

  const getNodeTypeColor = () => {
    if (!node) return 'bg-gray-100 text-gray-800';
    
    switch (node.type) {
      case 'START':
        return 'bg-green-100 text-green-800';
      case 'END':
        return 'bg-red-100 text-red-800';
      case 'CONDITION':
        return 'bg-yellow-100 text-yellow-800';
      case 'PARALLEL_START':
      case 'PARALLEL_END':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  if (!node) return null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className={`w-full ${getModalSize()} transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all`}>
                {/* Header */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                        Node Properties
                      </Dialog.Title>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getNodeTypeColor()}`}>
                        {node.type}
                        {node.data?.activityType && ` - ${node.data.activityType}`}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      onClick={handleCancel}
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Body */}
                <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                  <div className="space-y-6">
                    {/* Basic Properties */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Label
                        </label>
                        <input
                          type="text"
                          value={label}
                          onChange={(e) => handleFieldChange('label', e.target.value)}
                          disabled={isReadOnly}
                          placeholder={`Enter ${node.type?.toLowerCase() || 'node'} label`}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Description
                        </label>
                        <textarea
                          value={description}
                          onChange={(e) => handleFieldChange('description', e.target.value)}
                          disabled={isReadOnly}
                          rows={3}
                          placeholder="Enter a description for this node"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
                        />
                      </div>

                      {/* Node ID (Read-only) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Node ID
                        </label>
                        <input
                          type="text"
                          value={node.id}
                          disabled
                          className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm sm:text-sm"
                        />
                      </div>
                    </div>

                    {/* Activity-specific Configuration */}
                    {renderConfigurationForm() && (
                      <div className="pt-6 border-t border-gray-200">
                        <h4 className="text-sm font-medium text-gray-900 mb-4">
                          Activity Configuration
                        </h4>
                        {renderConfigurationForm()}
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <div className="text-xs text-gray-500">
                      {hasChanges && (
                        <span className="inline-flex items-center">
                          <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
                          Unsaved changes
                        </span>
                      )}
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                        onClick={handleCancel}
                      >
                        Cancel
                      </button>
                      {!isReadOnly && (
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={handleSave}
                          disabled={!hasChanges}
                        >
                          Save Changes
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PropertyModal;